import React, { useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

export default function CreateDriverDialog({ open, onOpenChange, onSuccess }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    vehicle_type: 'car',
    license_number: '',
    auto_approve: true,
    ready_to_accept: false
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.name || !formData.phone) {
      toast.error('Email, password, name, and phone are required');
      return;
    }

    setSubmitting(true);
    try {
      const params = new URLSearchParams(formData);
      await api.post(`/admin/drivers/create?${params}`);
      toast.success(`Driver created successfully!`);
      setFormData({ 
        email: '', password: '', name: '', phone: '', 
        vehicle_type: 'car', license_number: '', 
        auto_approve: true, ready_to_accept: false 
      });
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create driver');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Driver</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Full Name *</Label>
            <Input
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              placeholder="John Driver"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={e => setFormData({...formData, email: e.target.value})}
                placeholder="driver@example.com"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Phone *</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={e => setFormData({...formData, phone: e.target.value})}
                placeholder="+1-555-0300"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Password *</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={e => setFormData({...formData, password: e.target.value})}
              placeholder="Min 8 characters"
              minLength={8}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Vehicle Type *</Label>
              <Select value={formData.vehicle_type} onValueChange={value => setFormData({...formData, vehicle_type: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="car">Car</SelectItem>
                  <SelectItem value="motorcycle">Motorcycle</SelectItem>
                  <SelectItem value="bicycle">Bicycle</SelectItem>
                  <SelectItem value="scooter">Scooter</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>License Number</Label>
              <Input
                value={formData.license_number}
                onChange={e => setFormData({...formData, license_number: e.target.value})}
                placeholder="DL-123456"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="auto-approve"
                checked={formData.auto_approve}
                onCheckedChange={checked => setFormData({...formData, auto_approve: checked})}
              />
              <label htmlFor="auto-approve" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Auto-approve background check (BGC: Cleared)
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="ready-to-accept"
                checked={formData.ready_to_accept}
                onCheckedChange={checked => setFormData({...formData, ready_to_accept: checked})}
              />
              <label htmlFor="ready-to-accept" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Ready to Accept Orders (green badge)
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Driver'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
